import {
  faBars,
  faBookmark,
  faHeart,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Disclosure } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCollections } from "../../context/CollectionsContext";
import { useFavorites } from "../../context/FavoritesContext";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import Button from "../ui/Button";

const NavBar = () => {
  const navigation = [
    { name: "Home", href: "/" },
    { name: "Photos", href: "/photos" },
    { name: "Illustrations", href: "/illustrations" },
    { name: "Videos", href: "/videos" },
    { name: "Blog", href: "/blog" },
  ];

  const location = useLocation();
  const { user, logout } = useAuth();
  const { count } = useFavorites();
  const { count: collectionCount } = useCollections();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const navRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Close profile menu on outside click or Escape key
  useEffect(() => {
    if (!isProfileOpen) return undefined;
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProfileOpen]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    // Floating pill nav, detached from the top edge.
    <Disclosure
      as="nav"
      className="sticky top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4"
    >
      {({ open }) => (
        <div ref={navRef}>
          {/* Close the mobile menu on any click/tap outside the nav.
              v1.2.0's Disclosure has no `close()` render prop, so we toggle
              it shut by clicking the (open) menu button. */}
          <OutsideClose
            enabled={open}
            onOutside={() => menuButtonRef.current?.click()}
            containerRef={navRef}
          />
          <div className="max-w-6xl mx-auto rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-e1">
            <div className="flex items-center justify-between h-14 pl-5 pr-3">
              {/* brand */}
              <Link
                to="/"
                className="flex items-center min-h-[44px] text-2xl font-black tracking-tight text-gray-900 dark:text-white min-w-0 shrink"
              >
                Pixiy
              </Link>

              {/* center links */}
              <div className="hidden md:flex items-center gap-1">
                {navigation.map((item) => {
                  const isCurrent = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        isCurrent
                          ? "bg-gray-900 dark:bg-gray-700 text-white"
                          : "text-gray-500 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white",
                        "inline-flex items-center min-h-[44px] px-4 rounded-full text-sm font-semibold transition-colors duration-150",
                      )}
                      aria-current={isCurrent ? "page" : undefined}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              {/* right cluster */}
              {/* gap-2 is load-bearing: with 36px visuals and 44px tap targets
                  it's the smallest gap where adjacent hit areas don't overlap. */}
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to="/favorites"
                  aria-label="Favorites"
                  className="tap-target relative inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-500 dark:text-gray-300 hover:text-rose-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faHeart} />
                  {count > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-xs font-bold">
                      {count}
                    </span>
                  )}
                </Link>

                <Link
                  to="/collections"
                  aria-label="Collection"
                  className="tap-target relative inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-500 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faBookmark} />
                  {collectionCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-brand-600 text-white text-xs font-bold">
                      {collectionCount}
                    </span>
                  )}
                </Link>

                <ThemeToggle />

                {user ? (
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsProfileOpen((prev) => !prev)}
                      aria-expanded={isProfileOpen}
                      aria-haspopup="true"
                      aria-label={`User menu for ${user.displayName || "Account"}`}
                      title={user.displayName || "Account"}
                      className="tap-target relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-2 ring-brand-400/50 hover:ring-brand-500 transition-colors focus:outline-none"
                    >
                      <img
                        className="h-full w-full rounded-full object-cover bg-gray-200 dark:bg-gray-700 pointer-events-none"
                        src={
                          user.photoURL || "https://i.ibb.co/Db1FpSv/HI-THERE-2.png"
                        }
                        alt=""
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            "https://i.ibb.co/Db1FpSv/HI-THERE-2.png";
                        }}
                      />
                    </button>

                    {isProfileOpen && (
                      <div
                        className="absolute right-0 mt-3 w-56 origin-top-right rounded-2xl bg-white dark:bg-gray-800 shadow-e2 border border-gray-100 dark:border-gray-700 py-1.5 z-50"
                        role="menu"
                        aria-orientation="vertical"
                      >
                        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                          <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {user.displayName || "Signed in"}
                          </p>
                          {user.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {user.email}
                            </p>
                          )}
                        </div>

                        <div className="p-1">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setIsProfileOpen(false);
                              logout();
                            }}
                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-200 hover:bg-rose-50 dark:hover:bg-rose-900/30 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                          >
                            <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 text-rose-500" />
                            Log out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Button to="/login" variant="neutral" className="hidden sm:inline-flex font-bold">
                    Log in
                  </Button>
                )}

                {/* mobile menu button */}
                <Disclosure.Button
                  ref={menuButtonRef}
                  className="tap-target md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-500 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white transition-colors"
                >
                  <span className="sr-only">Open main menu</span>
                  <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* mobile panel */}
          <Disclosure.Panel className="md:hidden max-w-6xl mx-auto mt-2 rounded-3xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-e2 overflow-hidden">
            <div className="p-2 space-y-1">
              {navigation.map((item) => {
                const isCurrent = location.pathname === item.href;
                return (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className={classNames(
                      isCurrent
                        ? "bg-gray-900 dark:bg-gray-700 text-white"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                      "flex items-center min-h-[44px] px-4 py-2.5 rounded-2xl text-base font-semibold transition-colors",
                    )}
                    aria-current={isCurrent ? "page" : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                );
              })}
              {user ? (
                <Disclosure.Button
                  as="button"
                  type="button"
                  onClick={logout}
                  className="flex items-center gap-2.5 w-full min-h-[44px] px-4 py-2.5 rounded-2xl text-base font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4" />
                  Log out
                </Disclosure.Button>
              ) : (
                <Disclosure.Button
                  as={Link}
                  to="/login"
                  className="flex items-center min-h-[44px] px-4 py-2.5 rounded-2xl text-base font-bold text-brand-600 dark:text-brand-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Log in
                </Disclosure.Button>
              )}
            </div>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
};

// Closes the mobile menu when the user clicks/taps anywhere outside the nav.
// Renders nothing — just wires up the document listener while `enabled`.
const OutsideClose = ({ enabled, onOutside, containerRef }) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onOutside();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [enabled, onOutside, containerRef]);

  return null;
};

export default NavBar;
